import { useEffect, useState } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Trash2, Link2, Paperclip } from "lucide-react";
import {
  fmtINR, IDEA_STAGES, IDEA_CATEGORIES, computeScores,
} from "./bizdevUtils";

/** Iter 169 · Idea create/edit dialog with live ICE/RICE + notes. */

const BLANK = {
  title: "", summary: "", category: "revenue_stream", stage: "spark",
  venture_id: null, tags: [], revenue_low: 0, revenue_likely: 0,
  revenue_high: 0, revenue_type: "recurring_monthly", confidence_pct: 50,
  time_horizon_months: 3, reach: 5, impact: 5, effort: 5,
  next_action: "", next_action_due: null, start_date: null,
  target_date: null, private: false, revival_hint: "",
};

function Num({ label, value, onChange, min, max, testid }) {
  return (
    <div>
      <label className="text-[10px] text-muted-foreground uppercase tracking-widest">{label}</label>
      <Input type="number" min={min} max={max} value={value}
             onChange={(e) => onChange(e.target.value)}
             className="h-8 text-xs mt-0.5" data-testid={testid} />
    </div>
  );
}

export default function IdeaDialog({ wsId, idea, initialStage, ventures, onClose, onSaved }) {
  const [form, setForm] = useState(idea
    ? { ...idea, tags_text: (idea.tags || []).join(", ") }
    : { ...BLANK, stage: initialStage || "spark", tags_text: "" });
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [noteUrl, setNoteUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const isNew = !idea;

  useEffect(() => {
    if (idea?.id) {
      api.get(`/workspaces/${wsId}/bizdev/notes`,
        { params: { entity_type: "idea", entity_id: idea.id } })
        .then((r) => setNotes(r.data.rows || []))
        .catch(() => {});
    }
  }, [wsId, idea]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const scores = computeScores(form);
  const conf = Math.min(Math.max(Number(form.confidence_pct ?? 50), 0), 100) / 100;
  const weighted = (Number(form.revenue_likely) || 0) * conf;

  const payload = () => ({
    title: form.title, summary: form.summary, category: form.category,
    stage: form.stage, venture_id: form.venture_id || null,
    tags: (form.tags_text || "").split(",").map((s) => s.trim()).filter(Boolean),
    revenue_low: Number(form.revenue_low) || 0,
    revenue_likely: Number(form.revenue_likely) || 0,
    revenue_high: Number(form.revenue_high) || 0,
    revenue_type: form.revenue_type,
    confidence_pct: Number(form.confidence_pct) || 0,
    time_horizon_months: Number(form.time_horizon_months) || 0,
    reach: Number(form.reach) || 5, impact: Number(form.impact) || 5,
    effort: Number(form.effort) || 5,
    next_action: form.next_action || "",
    next_action_due: form.next_action_due || null,
    start_date: form.start_date || null,
    target_date: form.target_date || null,
    private: !!form.private,
    revival_hint: (form.revival_hint || "").trim(),
  });

  const isShelving = ["killed", "parked"].includes(form.stage) &&
                        !["killed", "parked"].includes(idea?.stage || "spark");
  const revivalHintLen = (form.revival_hint || "").trim().length;
  const needsRevivalHint = isShelving && revivalHintLen < 8 &&
                                (idea?.revival_hint || "").trim().length < 8;

  const save = async () => {
    if (!form.title?.trim()) { toast.error("Title is required"); return; }
    if (needsRevivalHint) {
      toast.error("Add a revival hint (≥ 8 chars) — what evidence would revive this idea?");
      return;
    }
    setBusy(true);
    try {
      if (isNew) await api.post(`/workspaces/${wsId}/bizdev/ideas`, payload());
      else await api.patch(`/workspaces/${wsId}/bizdev/ideas/${idea.id}`, payload());
      toast.success(isNew ? "Idea captured" : "Idea updated");
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this idea (and its notes/frameworks)?")) return;
    try {
      await api.delete(`/workspaces/${wsId}/bizdev/ideas/${idea.id}`);
      toast.success("Idea deleted");
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    try {
      const { data } = await api.post(`/workspaces/${wsId}/bizdev/notes`, {
        entity_type: "idea", entity_id: idea.id, body: noteText.trim(),
        kind: noteUrl ? "link" : "note", url: noteUrl || null,
      });
      setNotes((n) => [data, ...n]);
      setNoteText(""); setNoteUrl("");
    } catch (e) { toast.error(apiErrorMessage(e, "Note failed")); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="idea-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? "Capture idea" : "Edit idea"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <Input value={form.title} onChange={(e) => set("title", e.target.value)}
                 placeholder="Idea title — who pays, and why?"
                 className="text-sm font-medium" data-testid="idea-title-input" />
          <textarea value={form.summary || ""} onChange={(e) => set("summary", e.target.value)}
                    placeholder="Summary / the pitch in 2 sentences"
                    className="w-full h-16 text-xs p-2 border border-border rounded"
                    data-testid="idea-summary-input" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div>
              <label className="text-[10px] text-muted-foreground uppercase tracking-widest">Stage</label>
              <Select value={form.stage} onValueChange={(v) => set("stage", v)}>
                <SelectTrigger className="h-8 text-xs mt-0.5" data-testid="idea-stage-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {IDEA_STAGES.map((s) => (
                    <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground uppercase tracking-widest">Category</label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger className="h-8 text-xs mt-0.5" data-testid="idea-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {IDEA_CATEGORIES.map((c) => (
                    <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground uppercase tracking-widest">Venture</label>
              <Select value={form.venture_id || "_none"}
                      onValueChange={(v) => set("venture_id", v === "_none" ? null : v)}>
                <SelectTrigger className="h-8 text-xs mt-0.5" data-testid="idea-venture-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">— standalone —</SelectItem>
                  {ventures.map((v) => (
                    <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground uppercase tracking-widest">Tags</label>
              <Input value={form.tags_text} onChange={(e) => set("tags_text", e.target.value)}
                     placeholder="saas, b2b" className="h-8 text-xs mt-0.5"
                     data-testid="idea-tags-input" />
            </div>
          </div>

          <div className="border border-border p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                Revenue ballpark
              </span>
              <span className="text-xs font-mono text-emerald-700 font-bold"
                    data-testid="idea-weighted-preview">
                weighted: {fmtINR(weighted)}{form.revenue_type === "one_time" ? " once" : "/mo"}
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              <Num label="Low ₹" value={form.revenue_low} min={0}
                   onChange={(v) => set("revenue_low", v)} testid="idea-rev-low" />
              <Num label="Likely ₹" value={form.revenue_likely} min={0}
                   onChange={(v) => set("revenue_likely", v)} testid="idea-rev-likely" />
              <Num label="High ₹" value={form.revenue_high} min={0}
                   onChange={(v) => set("revenue_high", v)} testid="idea-rev-high" />
              <div>
                <label className="text-[10px] text-muted-foreground uppercase tracking-widest">Type</label>
                <Select value={form.revenue_type} onValueChange={(v) => set("revenue_type", v)}>
                  <SelectTrigger className="h-8 text-xs mt-0.5" data-testid="idea-rev-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="recurring_monthly">₹ / month</SelectItem>
                    <SelectItem value="annual">₹ / year</SelectItem>
                    <SelectItem value="one_time">One-time</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Num label="Horizon (mo)" value={form.time_horizon_months} min={0} max={60}
                   onChange={(v) => set("time_horizon_months", v)} testid="idea-horizon" />
            </div>
          </div>

          <div className="border border-border p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                Scoring (1–10)
              </span>
              <span className="text-xs font-mono" data-testid="idea-scores-preview">
                <span className="text-primary font-bold">ICE {scores.ice}</span>
                <span className="mx-2 text-muted-foreground">·</span>
                <span className="text-primary font-bold">RICE {scores.rice}</span>
              </span>
            </div>
            <div className="grid grid-cols-4 gap-2">
              <Num label="Reach" value={form.reach} min={1} max={10}
                   onChange={(v) => set("reach", v)} testid="idea-reach" />
              <Num label="Impact" value={form.impact} min={1} max={10}
                   onChange={(v) => set("impact", v)} testid="idea-impact" />
              <Num label="Effort" value={form.effort} min={1} max={10}
                   onChange={(v) => set("effort", v)} testid="idea-effort" />
              <Num label="Confidence %" value={form.confidence_pct} min={0} max={100}
                   onChange={(v) => set("confidence_pct", v)} testid="idea-confidence" />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2 items-end">
            <div className="col-span-2">
              <label className="text-[10px] text-muted-foreground uppercase tracking-widest">Next action</label>
              <Input value={form.next_action || ""} onChange={(e) => set("next_action", e.target.value)}
                     placeholder="Call 3 prospects to validate pricing"
                     className="h-8 text-xs mt-0.5" data-testid="idea-next-action" />
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground uppercase tracking-widest">Due</label>
              <Input type="date" value={(form.next_action_due || "").slice(0, 10)}
                     onChange={(e) => set("next_action_due", e.target.value || null)}
                     className="h-8 text-xs mt-0.5" data-testid="idea-next-action-due" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-muted-foreground uppercase tracking-widest">Roadmap start</label>
              <Input type="date" value={(form.start_date || "").slice(0, 10)}
                     onChange={(e) => set("start_date", e.target.value || null)}
                     className="h-8 text-xs mt-0.5" data-testid="idea-start-date" />
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground uppercase tracking-widest">Roadmap target</label>
              <Input type="date" value={(form.target_date || "").slice(0, 10)}
                     onChange={(e) => set("target_date", e.target.value || null)}
                     className="h-8 text-xs mt-0.5" data-testid="idea-target-date" />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Switch checked={!!form.private} onCheckedChange={(v) => set("private", v)}
                    data-testid="idea-private-switch" />
            <span className="text-xs text-muted-foreground">
              Private — only visible to you
            </span>
          </div>

          {(["killed", "parked"].includes(form.stage) || form.revival_hint) && (
            <div className={`border p-3 space-y-1.5 ${needsRevivalHint ? "border-amber-400 bg-amber-50" : "border-border"}`}
                 data-testid="idea-revival-hint-block">
              <label className="text-[10px] uppercase tracking-widest font-semibold flex items-center gap-1.5">
                🧟 Kill-criteria smart lock
                {needsRevivalHint && (
                  <span className="text-[9px] text-amber-700 font-mono">REQUIRED</span>
                )}
              </label>
              <textarea value={form.revival_hint || ""}
                          onChange={(e) => set("revival_hint", e.target.value)}
                          placeholder="What evidence would revive this idea? (e.g. 'If any competitor exits under $50k ARR, revive this')"
                          className="w-full h-14 text-xs p-2 border border-border rounded"
                          data-testid="idea-revival-hint-input" />
              <div className="text-[10px] text-muted-foreground">
                Feeds the AI Resurrection scanner as a first-class signal. {revivalHintLen}/8+ chars.
              </div>
            </div>
          )}

          {!isNew && (
            <div className="border-t border-border pt-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">
                Notes & research clips ({notes.length})
              </div>
              <div className="flex gap-1.5 mb-2">
                <Input value={noteText} onChange={(e) => setNoteText(e.target.value)}
                       placeholder="Add a note or research finding…"
                       className="h-8 text-xs flex-1" data-testid="idea-note-input" />
                <Input value={noteUrl} onChange={(e) => setNoteUrl(e.target.value)}
                       placeholder="URL (optional)"
                       className="h-8 text-xs w-40" data-testid="idea-note-url" />
                <Button size="sm" variant="outline" className="h-8 text-xs"
                        onClick={addNote} data-testid="idea-note-add-btn">
                  <Paperclip className="w-3 h-3" />
                </Button>
              </div>
              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                {notes.map((n) => (
                  <div key={n.id} className="text-xs border border-border p-2 bg-muted/30">
                    <div className="whitespace-pre-wrap">{n.body}</div>
                    <div className="flex items-center gap-2 mt-1 text-[10px] text-muted-foreground">
                      {n.url && (
                        <a href={n.url} target="_blank" rel="noreferrer"
                           className="text-primary hover:underline inline-flex items-center gap-0.5">
                          <Link2 className="w-2.5 h-2.5" /> source
                        </a>
                      )}
                      <span>{(n.created_at || "").slice(0, 10)}</span>
                      <span>·</span>
                      <span>{n.created_by_name || ""}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          {!isNew && (
            <Button variant="outline" size="sm" onClick={remove}
                    className="mr-auto text-red-600" data-testid="idea-delete-btn">
              <Trash2 className="w-3 h-3 mr-1" /> Delete
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={save} disabled={busy} data-testid="idea-save-btn">
            {busy ? "Saving…" : isNew ? "Capture idea" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
